import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { getUserNavigation } from '../../config/roleNavigation';
import { 
  Home, 
  Users, 
  GraduationCap, 
  Calendar, 
  FileText, 
  BarChart3, 
  Settings, 
  User,
  LogOut,
  Menu,
  X,
  Shield,
  Clock,
  UserPlus,
  Download,
  Award,
  FileSearch,
  Wrench,
  Database,
  Newspaper,
  BookOpen,
  Camera,
  Mail,
  Info,
  ClipboardList,
  Briefcase,
  FileCheck,
  UserCheck,
  Send,
  MessageCircle,
  MessageSquare,
  Building2,
  Upload,
  Heart
} from 'lucide-react';

const LayoutContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: ${props => props.theme.colors.background.primary};
  position: relative;
`;

const Sidebar = styled.aside`
  width: ${props => props.mobile ? '280px' : props.collapsed ? '80px' : '280px'};
  background-color: ${props => props.theme.colors.white};
  border-right: 1px solid ${props => props.theme.colors.border.light};
  transition: all 0.3s ease-in-out;
  display: flex;
  flex-direction: column;
  position: fixed;
  height: 100vh;
  z-index: 300;
  top: 0;
  left: 0;
  box-shadow: ${props => props.mobile ? '0 20px 50px rgba(15, 23, 42, 0.25)' : 'none'};
  transform: ${props => props.mobile ? (props.open ? 'translateX(0)' : 'translateX(-105%)') : 'translateX(0)'};
  border-radius: ${props => props.mobile ? '0 24px 24px 0' : '0'};
`;

const DrawerOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.4);
  z-index: 250;
  opacity: ${props => props.open ? 1 : 0};
  pointer-events: ${props => props.open ? 'auto' : 'none'};
  transition: opacity 0.3s ease-in-out;
`;

const SidebarHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid ${props => props.theme.colors.border.light};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const LogoIcon = styled.div`
  width: 60px;
  height: 60px;
  min-width: 60px;
  min-height: 60px;
  background-color: ${props => props.hasLogo ? 'transparent' : props.theme.colors.primary};
  border-radius: ${props => props.theme.borderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.white};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  font-size: ${props => props.theme.typography.fontSize.xl};
  flex-shrink: 0;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }
  
  span {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
  }
`;

const LogoText = styled.div`
  display: ${props => props.collapsed ? 'none' : 'block'};
  
  h1 {
    font-size: ${props => props.theme.typography.fontSize.lg};
    font-weight: ${props => props.theme.typography.fontWeight.bold};
    color: ${props => props.theme.colors.primary};
    margin: 0;
    font-family: ${props => props.theme.typography.fontFamily.heading};
  }
  
  p {
    font-size: ${props => props.theme.typography.fontSize.xs};
    color: ${props => props.theme.colors.text.secondary};
    margin: 0;
    font-family: ${props => props.theme.typography.fontFamily.primary};
  }
`;

const ToggleButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.text.secondary};
  cursor: pointer;
  padding: 0.5rem;
  border-radius: ${props => props.theme.borderRadius.md};
  transition: all 0.2s ease-in-out;
  display: ${props => props.mobile ? 'none' : 'inline-flex'};
  
  &:hover {
    background-color: ${props => props.theme.colors.gray[100]};
    color: ${props => props.theme.colors.text.primary};
  }
`;

const CloseDrawerButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.text.secondary};
  cursor: pointer;
  padding: 0.5rem;
  border-radius: ${props => props.theme.borderRadius.md};
  transition: all 0.2s ease-in-out;
  display: ${props => props.mobile ? 'inline-flex' : 'none'};
  
  &:hover {
    background-color: ${props => props.theme.colors.gray[100]};
    color: ${props => props.theme.colors.text.primary};
  }
`;

const Navigation = styled.nav`
  flex: 1;
  padding: 1rem 0;
  overflow-y: auto;
`;

const NavSectionLabel = styled.div`
  margin: 1rem 1rem 0.25rem;
  padding: 0.25rem 0.75rem;
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${props => props.theme.colors.text.secondary};
  opacity: 0.8;
`;

const NavItem = styled.div`
  margin: 0.25rem 1rem;
`;

const NavLink = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  background: none;
  border: none;
  border-radius: ${props => props.theme.borderRadius.md};
  color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.text.primary};
  background-color: ${props => props.active ? props.theme.colors.primary + '15' : 'transparent'};
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.active ? props.theme.typography.fontWeight.medium : props.theme.typography.fontWeight.normal};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  text-align: left;
  
  &:hover {
    background-color: ${props => props.active ? props.theme.colors.primary + '25' : props.theme.colors.gray[100]};
    color: ${props => props.theme.colors.primary};
  }
  
  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
`;

const NavText = styled.span`
  display: ${props => props.collapsed ? 'none' : 'block'};
`;

const UserSection = styled.div`
  padding: 1rem;
  border-top: 1px solid ${props => props.theme.colors.border.light};
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.theme.colors.background.secondary};
  margin-bottom: 0.75rem;
`;

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  background-color: ${props => props.theme.colors.primary};
  border-radius: ${props => props.theme.borderRadius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.white};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  font-size: ${props => props.theme.typography.fontSize.sm};
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const UserDetails = styled.div`
  display: ${props => props.collapsed ? 'none' : 'block'};
  flex: 1;
`;

const UserName = styled.div`
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.typography.fontFamily.primary};
`;

const UserRole = styled.div`
  font-size: ${props => props.theme.typography.fontSize.xs};
  color: ${props => props.theme.colors.text.secondary};
  font-family: ${props => props.theme.typography.fontFamily.primary};
`;

const MainContent = styled.main`
  flex: 1;
  margin-left: ${props => props.mobile ? '0' : props.sidebarCollapsed ? '80px' : '280px'};
  transition: margin-left 0.3s ease-in-out;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

const TopBar = styled.header`
  background-color: ${props => props.theme.colors.white};
  border-bottom: 1px solid ${props => props.theme.colors.border.light};
  padding: 1rem clamp(1rem, 3vw, 2rem);
  display: flex;
  align-items: center;
  gap: 1rem;
  position: sticky;
  top: 0;
  z-index: 200;
`;

const TopBarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
`;

const MobileMenuButton = styled.button`
  background-color: ${props => props.theme.colors.background.secondary};
  border: none;
  width: 44px;
  height: 44px;
  border-radius: ${props => props.theme.borderRadius.full};
  display: ${props => props.mobile ? 'inline-flex' : 'none'};
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.text.primary};
  box-shadow: ${props => props.theme.shadows.sm};
`;

const PageTitle = styled.h1`
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.typography.fontFamily.heading};
  margin: 0;
`;

const ContentArea = styled.div`
  flex: 1;
  padding: clamp(1rem, 2vw, 2rem);
  overflow-y: auto;
  min-height: 0;
`;

const Layout = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [websiteSettings, setWebsiteSettings] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { theme } = useTheme();
  const { user, logout, hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    fetchWebsiteSettings();
  }, []);
  
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 992;
      setIsMobile(mobile);
      if (!mobile) {
        setIsDrawerOpen(false);
      }
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const fetchWebsiteSettings = async () => {
    try {
      const { websiteSettingsAPI } = await import('../../services/api');
      const response = await websiteSettingsAPI.getSettings();
      if (response.data) {
        setWebsiteSettings(response.data);
      }
    } catch (error) {
      console.error('Error fetching website settings:', error);
    }
  };
  
  // Get role-based navigation
  const navigationItems = getUserNavigation(user?.role || 'member');
  
  // Icon mapping
  const iconMap = {
    Home,
    Users,
    GraduationCap,
    Calendar,
    FileText,
    BarChart3,
    Settings,
    Shield,
    Clock,
    UserPlus,
    UserCheck,
    Download,
    Award,
    FileSearch,
    Wrench,
    Database,
    Newspaper,
    BookOpen,
    Camera,
    Mail,
    Info,
    ClipboardList,
    Briefcase,
    FileCheck,
    Send,
    MessageCircle,
    MessageSquare,
    Building2,
    Upload,
    Heart
  };
  
  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  
  const handleNavClick = (path) => {
    navigate(path);
    if (isMobile) {
      setIsDrawerOpen(false);
    }
  };
  
  const getUserInitials = () => {
    if (!user) return 'U';
    return `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase();
  };
  
  const getRoleDisplayName = (role) => {
    const roleNames = {
      system_admin: 'System Administrator',
      regional_coordinator: 'Regional Coordinator',
      data_entry_officer: 'Data Entry Officer',
      program_officer: 'Program Officer',
      member: 'Member',
      super_admin: 'Super Administrator'
    };
    return roleNames[role] || role;
  };
  
  return (
    <LayoutContainer theme={theme}>
      {isMobile && <DrawerOverlay open={isDrawerOpen} onClick={() => setIsDrawerOpen(false)} />}
      <Sidebar
        theme={theme}
        collapsed={sidebarCollapsed}
        mobile={isMobile}
        open={isDrawerOpen}
      >
        <SidebarHeader theme={theme}>
          <Logo>
            <LogoIcon theme={theme} hasLogo={!!websiteSettings?.logo_path}>
              {websiteSettings?.logo_path ? (
                <img 
                  src={websiteSettings.logo_path} 
                  alt={websiteSettings.website_name || "EWHRDN Logo"}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    if (e.target.nextElementSibling) {
                      e.target.nextElementSibling.style.display = 'flex';
                    }
                  }}
                />
              ) : null}
              <span style={{ display: websiteSettings?.logo_path ? 'none' : 'flex' }}>E</span>
            </LogoIcon>
            <LogoText theme={theme} collapsed={sidebarCollapsed}>
              <h1>{websiteSettings?.website_name || 'EWHRDN'}</h1>
              <p>Membership System</p>
            </LogoText>
          </Logo>
          {!isMobile ? (
            <ToggleButton
              theme={theme}
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            >
              {sidebarCollapsed ? <Menu size={20} /> : <X size={20} />}
            </ToggleButton>
          ) : (
            <CloseDrawerButton theme={theme} mobile onClick={() => setIsDrawerOpen(false)}>
              <X size={20} />
            </CloseDrawerButton>
          )}
        </SidebarHeader>
        
        <Navigation>
          {navigationItems.map((item) => {
            // Section headers (non-clickable group labels)
            if (item.type === 'section') {
              // Only show section if user has permission for its area
              if (item.permission && !hasPermission(item.permission)) {
                return null;
              }
              return (
                <NavSectionLabel key={`section-${item.label}`} theme={theme}>
                  {item.label}
                </NavSectionLabel>
              );
            }

            // Regular navigation items
            if (!hasPermission(item.permission)) {
              return null;
            }
            
            const Icon = iconMap[item.icon] || FileText; // Fallback to FileText if icon not found
            const isActive = location.pathname === item.path || 
                           (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
            
            return (
              <NavItem key={item.path}>
                <NavLink
                  theme={theme}
                  active={isActive}
                  onClick={() => handleNavClick(item.path)}
                >
                  {Icon && <Icon />}
                  <NavText theme={theme} collapsed={sidebarCollapsed}>
                    {item.label}
                  </NavText>
                </NavLink>
              </NavItem>
            );
          })}
        </Navigation>
        
        <UserSection theme={theme}>
          <UserInfo theme={theme}>
            <UserAvatar theme={theme}>
              {user?.profile_photo_url ? (
                <img src={user.profile_photo_url} alt="Profile" />
              ) : (
                getUserInitials()
              )}
            </UserAvatar>
            <UserDetails theme={theme} collapsed={sidebarCollapsed}>
              <UserName theme={theme}>
                {user?.first_name} {user?.last_name}
              </UserName>
              <UserRole theme={theme}>
                {getRoleDisplayName(user?.role)}
              </UserRole>
            </UserDetails>
          </UserInfo>
          
          <NavItem>
            <NavLink
              theme={theme}
              onClick={() => handleNavClick('/app/profile')}
            >
              <User />
              <NavText theme={theme} collapsed={sidebarCollapsed}>
                Profile
              </NavText>
            </NavLink>
          </NavItem>
          
          <NavItem>
            <NavLink
              theme={theme}
              onClick={handleLogout}
            >
              <LogOut />
              <NavText theme={theme} collapsed={sidebarCollapsed}>
                Logout
              </NavText>
            </NavLink>
          </NavItem>
        </UserSection>
      </Sidebar>
      
      <MainContent theme={theme} sidebarCollapsed={sidebarCollapsed} mobile={isMobile}>
        <TopBar theme={theme}>
          <TopBarLeft>
            <MobileMenuButton
              theme={theme}
              mobile={isMobile}
              onClick={() => setIsDrawerOpen(true)}
            >
              <Menu size={20} />
            </MobileMenuButton>
            <PageTitle theme={theme}>
            {navigationItems.find(item => 
              location.pathname === item.path || 
              (item.path !== '/dashboard' && location.pathname.startsWith(item.path))
            )?.label || 'Dashboard'}
            </PageTitle>
          </TopBarLeft>
        </TopBar>
        
        <ContentArea theme={theme}>
          <Outlet />
        </ContentArea>
      </MainContent>
    </LayoutContainer>
  );
};

export default Layout;
